import { useState, useId, useMemo } from 'react';
import { isValidEmail } from 'utils/validation';
import pull from 'lodash/pull';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { ORG_PERMISSIONS } from 'utils/permissions';
import { Toggle } from 'ui/toggle';
import { CheckboxGroup } from 'ui/checkboxGroup';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES } from 'ui/modal';
import { PrimaryButton } from 'ui/button';
import { MAX_FULL_NAME_LENGTH, MAX_EMAIL_LENGTH } from '../constants';
import { StyledWarningBanner, CheckmarkContainer, CheckboxContainer } from './Organization.styled';

type CreateMemberModalProps = {
  closeCreateMemberModal: () => void;
  isUserOrgAdmin: boolean;
  onCreate: (obj: any) => void;
  organizationPermissionsForForm?: Record<string, any>[];
};

export const CreateMemberModal = ({
  closeCreateMemberModal,
  isUserOrgAdmin,
  onCreate,
  organizationPermissionsForForm = [],
}: CreateMemberModalProps) => {
  const formId = useId();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [orgAdmin, setOrgAdmin] = useState(false);
  const [organizationPermissions, setOrganizationPermissions] = useState<number[]>([]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onCreate({
      firstName,
      lastName,
      email,
      orgAdmin,
      organizationPermissions: orgAdmin ? Object.values(ORG_PERMISSIONS) : organizationPermissions,
    });

    closeCreateMemberModal();
  };

  const checkboxGroupOptions = useMemo(
    () =>
      organizationPermissionsForForm.map((option) => {
        return {
          id: option.id,
          name: option.name,
          setChecked: () => {
            if (organizationPermissions.includes(option.id)) {
              setOrganizationPermissions([...pull(organizationPermissions, option.id)]);
            } else {
              setOrganizationPermissions([...organizationPermissions, option.id]);
            }
          },
          label: option.name,
          value: option.id,
          isDisabled: orgAdmin,
          status: organizationPermissions.includes(option.id) || orgAdmin,
        };
      }),
    [organizationPermissionsForForm, orgAdmin, organizationPermissions],
  );

  return (
    <Modal id={useId()} isModalOpen onClose={closeCreateMemberModal} size={MODAL_SIZES.SMALL}>
      <ModalHeader>Create new member</ModalHeader>
      <ModalBody>
        <form id={formId} onSubmit={handleSubmit}>
          <FormField labelText="First name">
            <InputText
              autoFocus
              name={'firstName'}
              value={firstName}
              onChange={(newFirstName) => setFirstName(newFirstName)}
              maxLength={MAX_FULL_NAME_LENGTH}
            />
          </FormField>
          <FormField labelText="Last name">
            <InputText
              name={'lastName'}
              value={lastName}
              onChange={(newLastName) => setLastName(newLastName)}
              maxLength={MAX_FULL_NAME_LENGTH}
            />
          </FormField>
          <FormField labelText="Email">
            <InputText
              name={'email'}
              value={email}
              onChange={(newEmail) => setEmail(newEmail)}
              maxLength={MAX_EMAIL_LENGTH}
            />
          </FormField>
          {isUserOrgAdmin && (
            <>
              <StyledWarningBanner Icon={null} data-test="org-admin-toggle">
                <FormField labelText="Org admin?">
                  <CheckmarkContainer>
                    <div>
                      Org admins have all organization permissions and are automatically added to
                      all projects as admins.
                    </div>
                    <Toggle
                      data-test="org-admin-toggle-button"
                      name="orgAdmin"
                      isActive={orgAdmin}
                      onClick={(newState) => newState !== undefined && setOrgAdmin(newState)}
                    />
                  </CheckmarkContainer>
                </FormField>
              </StyledWarningBanner>
              <FormField labelText="Org permissions" data-test="org-permissions">
                <CheckboxContainer>
                  <CheckboxGroup
                    isDisabled={orgAdmin}
                    name="orgPermissions"
                    options={checkboxGroupOptions}
                  />
                </CheckboxContainer>
              </FormField>
            </>
          )}
        </form>
      </ModalBody>
      <ModalFooter onClose={closeCreateMemberModal}>
        <PrimaryButton
          data-test="create-member-modal-submit-button"
          form={formId}
          isDisabled={!firstName || !lastName || [...isValidEmail(email)].length > 0}
          type="submit"
        >
          Create
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
